import { ApiError, apiErrorMessage } from "@/lib/api";
import { i18n } from "@/lib/i18n";
import { useCallback, useRef, useState } from "react";

/**
 * The write-side counterpart to the `api` fetch wrapper. Every mutation
 * handler in the app was re-writing the same ceremony by hand:
 *
 *   const [saving, setSaving] = useState(false);
 *   const [error, setError] = useState<string | null>(null);
 *   async function submit() {
 *     setSaving(true); setError(null);
 *     try { await api.post(...); onSuccess() }
 *     catch (err) { setError(err instanceof ApiError ? err.message : "…") }
 *     finally { setSaving(false) }
 *   }
 *
 * `useApiMutation` owns that ceremony — the pending flag, the
 * error-message extraction (server `error` string via `ApiError`, generic
 * fallback otherwise), and the try/catch/finally — so a handler is just
 * the request plus what to do on success/failure.
 *
 * It stays idiom-agnostic on purpose: the returned `error` string serves
 * inline error banners, while `onError` serves the toast-based handlers.
 * `onSuccess`/`onError` see the latest closures (kept in refs), so a
 * handler can safely reference current form state without re-subscribing.
 */

export type UseApiMutationOptions<TArgs extends unknown[], TResult> = {
  /** Runs after the request resolves — navigate, invalidate, toast, reset. */
  onSuccess?: (result: TResult, ...args: TArgs) => void | Promise<void>;
  /** Runs on failure with the resolved message. Use for toast handlers;
   *  omit to rely on the returned `error` string instead. */
  onError?: (message: string, err: unknown) => void;
  /** Overrides the fallback message for non-`ApiError` failures. */
  fallbackMessage?: string;
  /** Full control over the surfaced message — for handlers that map
   *  failures to a bespoke friendly string regardless of the server's
   *  own `error`. Takes precedence over the default extraction. */
  errorMessage?: (err: unknown) => string;
};

export type UseApiMutationResult<TArgs extends unknown[], TResult> = {
  /** Fire the request. Resolves to the result, or `undefined` on failure
   *  (the error is surfaced via `error` / `onError`, never re-thrown). */
  mutate: (...args: TArgs) => Promise<TResult | undefined>;
  /** True while the request is in flight — wire into button `disabled`. */
  pending: boolean;
  /** Last failure's message, or `null`. Cleared at the start of each run. */
  error: string | null;
  /** Surface a message in the same channel as request failures — for
   *  client-side pre-flight validation ("Name is required.") before the
   *  request is even attempted. */
  setError: (message: string | null) => void;
  /** Manually clear the error (e.g. when the user edits the form). */
  clearError: () => void;
};

export function useApiMutation<TArgs extends unknown[] = [], TResult = unknown>(
  fn: (...args: TArgs) => Promise<TResult>,
  options?: UseApiMutationOptions<TArgs, TResult>,
): UseApiMutationResult<TArgs, TResult> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Latest closures without re-creating `mutate` — handlers can read
  // current form state in fn/onSuccess without a stale snapshot.
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutate = useCallback(async (...args: TArgs): Promise<TResult | undefined> => {
    setPending(true);
    setError(null);
    try {
      const result = await fnRef.current(...args);
      await optionsRef.current?.onSuccess?.(result, ...args);
      return result;
    } catch (err) {
      const opts = optionsRef.current;
      const message = opts?.errorMessage
        ? opts.errorMessage(err)
        : err instanceof ApiError
          ? apiErrorMessage(err)
          : (opts?.fallbackMessage ?? i18n.t("miscLib.genericError"));
      setError(message);
      opts?.onError?.(message, err);
      return undefined;
    } finally {
      setPending(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { mutate, pending, error, setError, clearError };
}

export type UseRowMutationOptions<Args extends unknown[], TResult> = {
  /** Row key this call targets — drives the per-row pending flag. */
  rowKey: (...args: Args) => string;
  onSuccess?: (result: TResult, ...args: Args) => void | Promise<void>;
  onError?: (message: string, ...args: Args) => void;
  errorMessage?: (err: unknown) => string;
};

export type UseRowMutationResult<Args extends unknown[]> = {
  /** Fire the request for a row. Never rejects — failures go to `onError`. */
  run: (...args: Args) => Promise<void>;
  /** The row key currently in flight, or `null`. One row at a time. */
  runningKey: string | null;
  /** `runningKey === key` — a specific row's spinner / disabled state. */
  isRunning: (key: string) => boolean;
  /** Any row in flight — "disable the whole group while one runs". */
  busy: boolean;
};

/**
 * The list counterpart to `useApiMutation`: a per-row action where the
 * pending flag is keyed by which row is in flight (Analyse this email,
 * Send this batch, Trigger this sync). Handlers were all hand-rolling
 * the same `const [runningKey, setRunningKey] = useState<…|null>` +
 * try/catch/finally; this owns it. One row runs at a time (matches the
 * existing single-`null` state); the success/error side-effects (toast,
 * invalidate, optimistic patch) live in `onSuccess`/`onError`.
 */
export function useRowMutation<Args extends unknown[], TResult = unknown>(
  fn: (...args: Args) => Promise<TResult>,
  options: UseRowMutationOptions<Args, TResult>,
): UseRowMutationResult<Args> {
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const run = useCallback(async (...args: Args): Promise<void> => {
    setRunningKey(optionsRef.current.rowKey(...args));
    try {
      const result = await fnRef.current(...args);
      await optionsRef.current.onSuccess?.(result, ...args);
    } catch (err) {
      const opts = optionsRef.current;
      const message = opts.errorMessage
        ? opts.errorMessage(err)
        : err instanceof ApiError
          ? apiErrorMessage(err)
          : i18n.t("miscLib.genericError");
      opts.onError?.(message, ...args);
    } finally {
      setRunningKey(null);
    }
  }, []);

  const isRunning = useCallback((key: string) => runningKey === key, [runningKey]);

  return { run, runningKey, isRunning, busy: runningKey !== null };
}
